'use client'

import { ContentFilter } from '@/components/common/content-filter/content-filter'
import { SortOption } from '@/components/common/content-filter/use-content-filter'
import { ProductCardWrap } from '@/components/content-types/product/product-card-wrap'
import { ProductItem } from '@/lib/types/operations/product'

interface Category {
  name: string
  slug: string
}

interface Props {
  products: ProductItem[]
  categories: Category[]
  categoryMap: Record<string, string>
  priceTier: 'RETAIL' | 'WHOLESALE'
}

const sortOptions: SortOption<ProductItem>[] = [
  {
    label: 'Newest',
    value: 'newest',
    compareFn: (a, b) =>
      new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime(),
  },
  {
    label: 'Oldest',
    value: 'oldest',
    compareFn: (a, b) =>
      new Date(a.createdAt as any).getTime() - new Date(b.createdAt as any).getTime(),
  },
  {
    label: 'A–Z',
    value: 'az',
    compareFn: (a, b) => (a.name || '').localeCompare(b.name || ''),
  },
  {
    label: 'Z–A',
    value: 'za',
    compareFn: (a, b) => (b.name || '').localeCompare(a.name || ''),
  },
  {
    label: 'Price: Low–High',
    value: 'price-asc',
    compareFn: (a, b) => (a.price || 0) - (b.price || 0),
  },
  {
    label: 'Price: High–Low',
    value: 'price-desc',
    compareFn: (a, b) => (b.price || 0) - (a.price || 0),
  },
]

export function ProductsClient({ products, categories, categoryMap, priceTier }: Props) {
  return (
    <ContentFilter
      items={products}
      categories={categories}
      sortOptions={sortOptions}
      searchFields={['name', 'description']}
      categoryAccessor={(p) => (p as any).categorySlug as string | null}
      searchPlaceholder="Search products..."
      renderItems={(filtered) => (
        <div className="mx-auto max-w-[1200px] px-6 py-10">
          <p className="cw-eyebrow mb-4">
            {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((product) => (
              <ProductCardWrap
                key={product.id}
                product={product}
                priceTier={priceTier}
                categoryName={product.categoryId ? categoryMap[product.categoryId] : undefined}
              />
            ))}
          </div>
        </div>
      )}
    />
  )
}
